import { useId, useState } from 'react';
import { Users } from 'lucide-react';
import {
  PagedSellerContactsPicker,
  SelectContactsToggle,
} from '~/components/common/SellerContactsPicker';
import { selectableCardContainerClassName } from '~/components/common/sellerRowClasses';
import { SellerRowHeader } from '~/components/common/SellerRowHeader';
import type { PickableSellerOrganization } from '~/components/common/SellerOrganizationPicker';
import type { ContactSelection } from '~/components/common/useSellerRecipients';
import { cn, sellerLabel } from '~/utils';
import type { Contact } from '~/utils/contact';

export type SellerContactsRowLabels = {
  contactCount: (count: number) => string;
  noContacts: string;
  selectedCount: (count: number) => string;
  selectOrganization: (organizationLabel: string) => string;
};

export type SellerContactsRowRecipients = {
  isOrganizationSelected: (organizationId: string) => boolean;
  toggleOrganization: (organization: PickableSellerOrganization) => void;
  contactSelectionOf: (organizationId: string) => ContactSelection;
  chooseContacts: (
    organization: PickableSellerOrganization,
    contacts: Contact[],
    primaryContactId: string | undefined
  ) => void;
};

type SellerContactsRowProps = {
  organization: PickableSellerOrganization;
  recipients: SellerContactsRowRecipients;
  labels: SellerContactsRowLabels;
  testId: string;
  onContactAdded: () => void;
  primaryContactMode?: 'optional' | 'off';
  disabled?: boolean;
};

export function SellerContactsRow({
  organization,
  recipients,
  labels,
  testId,
  onContactAdded,
  primaryContactMode = 'optional',
  disabled = false,
}: Readonly<SellerContactsRowProps>) {
  const pickerId = useId();
  const subtitleId = useId();
  const selectedCountId = useId();
  const [expanded, setExpanded] = useState(false);
  const organizationLabel = sellerLabel(organization);
  const contactCount = organization.sellerContactCount;
  const organizationSelected = recipients.isOrganizationSelected(
    organization.id
  );
  const { contactIds, primaryContactId } = recipients.contactSelectionOf(
    organization.id
  );
  const selectedCountLabel =
    contactIds.length > 0 ? labels.selectedCount(contactIds.length) : undefined;

  return (
    <li
      className={selectableCardContainerClassName(organizationSelected)}
      data-testid={testId}
    >
      <div className="flex items-center gap-3 p-1.5 pr-4">
        <button
          type="button"
          aria-pressed={organizationSelected}
          aria-label={labels.selectOrganization(organizationLabel)}
          aria-describedby={
            selectedCountLabel === undefined
              ? subtitleId
              : `${subtitleId} ${selectedCountId}`
          }
          disabled={disabled}
          onClick={() => recipients.toggleOrganization(organization)}
          data-testid={`${testId}-select`}
          className={cn(
            'flex min-w-0 flex-1 items-center gap-3 rounded-lg px-2.5 py-1.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-prim-900 focus-visible:ring-offset-2',
            disabled
              ? 'cursor-not-allowed opacity-60'
              : 'cursor-pointer hover:bg-neut-50'
          )}
        >
          <SellerRowHeader
            organizationLabel={organizationLabel}
            subtitleIcon={Users}
            subtitle={
              contactCount > 0
                ? labels.contactCount(contactCount)
                : labels.noContacts
            }
            subtitleId={subtitleId}
          />
        </button>
        {selectedCountLabel !== undefined && (
          <span
            id={selectedCountId}
            data-testid={`${testId}-selected-count`}
            className={cn(
              'shrink-0 rounded-full bg-prim-600 px-2.5 py-1 text-xs font-semibold text-neut-0',
              disabled ? 'opacity-60' : undefined
            )}
          >
            {selectedCountLabel}
          </span>
        )}
        <SelectContactsToggle
          expanded={expanded}
          onToggle={() => setExpanded(!expanded)}
          controlsId={pickerId}
          sellerName={organizationLabel}
          disabled={disabled}
          testId={`${testId}-select-contacts`}
        />
      </div>
      {expanded && (
        <PagedSellerContactsPicker
          id={pickerId}
          organizationId={organization.id}
          sellerName={organizationLabel}
          selectedContactIds={contactIds}
          primaryContactId={primaryContactId}
          primaryContactMode={primaryContactMode}
          onChange={(selectedContacts, selectedPrimaryContactId) =>
            recipients.chooseContacts(
              organization,
              selectedContacts,
              selectedPrimaryContactId
            )
          }
          onContactAdded={onContactAdded}
          disabled={disabled}
          testId={`${testId}-contacts`}
        />
      )}
    </li>
  );
}
